import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { CheckCircle, XCircle, Edit, Trash2, Plus, Star, DollarSign, Loader } from 'lucide-react';

export default function SubscriptionPlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    tier_name: '',
    display_name: '',
    monthly_price: '',
    annual_price: '',
    monthly_api_calls: 10000,
    daily_rate_limit: 1000,
    features: {},
    support_tier: 'email',
    is_active: true,
    stripe_product_id: '',
    stripe_monthly_price_id: '',
    stripe_yearly_price_id: '',
    max_portfolios: ''
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/subscription-plans/', {
        params: { include_inactive: true }
      });
      setPlans(response);
      setError(null);
    } catch (err) {
      console.error('Error fetching subscription plans:', err);
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      tier_name: plan.tier_name,
      display_name: plan.display_name,
      monthly_price: plan.monthly_price,
      annual_price: plan.annual_price || '',
      monthly_api_calls: plan.monthly_api_calls || 10000,
      daily_rate_limit: plan.daily_rate_limit || 1000,
      features: plan.features || {},
      support_tier: plan.support_tier || 'email',
      is_active: plan.is_active,
      stripe_product_id: plan.stripe_product_id || '',
      stripe_monthly_price_id: plan.stripe_monthly_price_id || '',
      stripe_yearly_price_id: plan.stripe_yearly_price_id || '',
      max_portfolios: plan.max_portfolios || ''
    });
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedPlan(null);
    setFormData({
      tier_name: '',
      display_name: '',
      monthly_price: '',
      annual_price: '',
      monthly_api_calls: 10000,
      daily_rate_limit: 1000,
      features: {},
      support_tier: 'email',
      is_active: true,
      stripe_product_id: '',
      stripe_monthly_price_id: '',
      stripe_yearly_price_id: '',
      max_portfolios: ''
    });
    setShowModal(true);
  };

  const handleDelete = async (tier_id) => {
    if (!window.confirm(`Are you sure you want to deactivate this plan?`)) {
      return;
    }

    try {
      await api.delete(`/subscription-plans/${tier_id}`);
      await fetchPlans();
    } catch (err) {
      console.error('Error deleting plan:', err);
      alert('Failed to deactivate plan: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (selectedPlan) {
        // Update existing plan
        await api.put(`/subscription-plans/${selectedPlan.tier_id}`, {
          display_name: formData.display_name,
          monthly_price: parseFloat(formData.monthly_price),
          annual_price: formData.annual_price ? parseFloat(formData.annual_price) : null,
          monthly_api_calls: parseInt(formData.monthly_api_calls),
          daily_rate_limit: parseInt(formData.daily_rate_limit),
          features: formData.features,
          support_tier: formData.support_tier,
          is_active: formData.is_active,
          stripe_product_id: formData.stripe_product_id || null,
          stripe_monthly_price_id: formData.stripe_monthly_price_id || null,
          stripe_yearly_price_id: formData.stripe_yearly_price_id || null,
          max_portfolios: formData.max_portfolios ? parseInt(formData.max_portfolios) : null
        });
      } else {
        // Create new plan
        await api.post('/subscription-plans/', {
          tier_name: formData.tier_name,
          display_name: formData.display_name,
          monthly_price: parseFloat(formData.monthly_price),
          annual_price: formData.annual_price ? parseFloat(formData.annual_price) : null,
          monthly_api_calls: parseInt(formData.monthly_api_calls),
          daily_rate_limit: parseInt(formData.daily_rate_limit),
          features: formData.features,
          support_tier: formData.support_tier,
          is_active: formData.is_active,
          stripe_product_id: formData.stripe_product_id || null,
          stripe_monthly_price_id: formData.stripe_monthly_price_id || null,
          stripe_yearly_price_id: formData.stripe_yearly_price_id || null,
          max_portfolios: formData.max_portfolios ? parseInt(formData.max_portfolios) : null
        });
      }

      setShowModal(false);
      await fetchPlans();
    } catch (err) {
      console.error('Error saving plan:', err);
      alert('Failed to save plan: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Extract features list from features object for display
  const getFeaturesArray = (features) => {
    if (!features) return [];
    if (Array.isArray(features.features)) return features.features;
    if (typeof features === 'object') {
      return Object.entries(features)
        .filter(([key, value]) => key !== 'features')
        .map(([key, value]) => `${key}: ${value}`);
    }
    return [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subscription Plans</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage subscription plans for API users
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Plan
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.tier_id}
            className={`bg-white rounded-lg shadow-md p-6 border-2 border-gray-200 ${
              !plan.is_active ? 'opacity-60' : ''
            }`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold text-gray-900">{plan.display_name}</h3>
                </div>
                <p className="text-sm text-gray-600">{plan.tier_name}</p>
              </div>
            </div>

            {/* Price */}
            <div className="mb-4">
              <div className="flex items-baseline">
                <span className="text-4xl font-bold text-gray-900">
                  ${plan.monthly_price}
                </span>
                <span className="ml-2 text-gray-600">/month</span>
              </div>
              {plan.annual_price && (
                <div className="text-sm text-gray-600 mt-1">
                  ${plan.annual_price}/year (save{' '}
                  {Math.round(((plan.monthly_price * 12 - plan.annual_price) / (plan.monthly_price * 12)) * 100)}%)
                </div>
              )}
            </div>

            {/* Rate Limits */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="text-sm text-gray-600">API Limits</div>
              <div className="font-semibold text-primary-600">
                {plan.monthly_api_calls?.toLocaleString()} calls/month
              </div>
              <div className="text-sm text-gray-500">
                {plan.daily_rate_limit?.toLocaleString()} calls/day
              </div>
            </div>

            {/* Features */}
            <div className="mb-4">
              <div className="text-sm font-semibold text-gray-700 mb-2">Features:</div>
              <ul className="space-y-1">
                {getFeaturesArray(plan.features).slice(0, 5).map((feature, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              {plan.max_portfolios && (
                <div className="text-sm text-gray-600 mt-2">
                  Max portfolios: {plan.max_portfolios}
                </div>
              )}
            </div>

            {/* Status Badges */}
            <div className="flex items-center gap-2 mb-4">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  plan.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {plan.is_active ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                {plan.is_active ? 'Active' : 'Inactive'}
              </span>
              <span className="text-xs text-gray-500 uppercase">{plan.support_tier}</span>
            </div>

            {/* Stripe Info */}
            {plan.stripe_product_id && (
              <div className="text-xs text-gray-500 mb-4 truncate">
                Stripe: {plan.stripe_product_id}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleEdit(plan)}
                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </button>
              {plan.is_active && (
                <button
                  onClick={() => handleDelete(plan.tier_id)}
                  className="inline-flex items-center justify-center px-3 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {selectedPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Tier Name (only for new plans) */}
                {!selectedPlan && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tier Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.tier_name}
                      onChange={(e) => setFormData({ ...formData, tier_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., personal, professional, advisor"
                    />
                  </div>
                )}

                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Pricing */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Price ($) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.monthly_price}
                      onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Annual Price ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.annual_price}
                      onChange={(e) => setFormData({ ...formData, annual_price: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                {/* Rate Limits */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly API Calls *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.monthly_api_calls}
                      onChange={(e) => setFormData({ ...formData, monthly_api_calls: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Daily Rate Limit *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.daily_rate_limit}
                      onChange={(e) => setFormData({ ...formData, daily_rate_limit: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                {/* Support Tier */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Support Tier
                    </label>
                    <select
                      value={formData.support_tier}
                      onChange={(e) => setFormData({ ...formData, support_tier: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="email">Email</option>
                      <option value="email_slack">Email + Slack</option>
                      <option value="priority">Priority</option>
                      <option value="dedicated">Dedicated</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Portfolios
                    </label>
                    <input
                      type="number"
                      value={formData.max_portfolios}
                      onChange={(e) => setFormData({ ...formData, max_portfolios: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Leave empty for unlimited"
                    />
                  </div>
                </div>

                {/* Stripe IDs */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stripe Product ID
                    </label>
                    <input
                      type="text"
                      value={formData.stripe_product_id}
                      onChange={(e) => setFormData({ ...formData, stripe_product_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Stripe Monthly Price ID
                      </label>
                      <input
                        type="text"
                        value={formData.stripe_monthly_price_id}
                        onChange={(e) => setFormData({ ...formData, stripe_monthly_price_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Stripe Yearly Price ID
                      </label>
                      <input
                        type="text"
                        value={formData.stripe_yearly_price_id}
                        onChange={(e) => setFormData({ ...formData, stripe_yearly_price_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Active Flag */}
                <div className="flex items-center gap-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    {selectedPlan ? 'Update Plan' : 'Create Plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
